/**
 * LAYOUTS
 */
import TranslationLayout from '@/main/layouts/translation.vue';
import AutoDamageLayout from '@/main/layouts/autodamage.vue';
import SharedEntityLayout from '@/main/layouts/sharedentity.vue';
import RiskLayout from '@/main/layouts/riskspace.vue';

/**
 * PAGES
 */
import vuexStore from '@/store/store';
import fourofour from '@/main/components/404.vue';
// import feeds from '@/main/components/feeds';
// LAZY LOADING
const feeds = () => import(/* webpackChunkName: "feeds" */ '@/main/components/feeds/index.vue');
const dashboard = () =>
    import(/* webpackChunkName: "dashboard" */ '@/main/components/dashboard.vue');
const customers = () =>
    import(/* webpackChunkName: "customers" */ '@/main/components/clients/customers.vue');
const customerDetail = () =>
    import(
        /* webpackChunkName: "customersDetail" */ '@/main/components/clients/customerdetail.vue'
    );
const microlist = () =>
    import(/* webpackChunkName: "microlist" */ '@/main/components/microlist.vue');
const microlistitemdetail = () =>
    import(
        /* webpackChunkName: "microlistitemdetail" */ '@/main/components/microlistitemdetail.vue'
    );
const conversations = () =>
    import(/* webpackChunkName: "conversations" */ '@/main/components/conversations.vue');
const appointments = () =>
    import(/* webpackChunkName: "appointments" */ '@/main/components/appointments.vue');
const conversationdetail = () =>
    import(/* webpackChunkName: "conversationdetail" */ '@/main/components/conversationdetail.vue');
const representativedetail = () =>
    import(
        /* webpackChunkName: "representativedetail" */ '@/main/components/representativedetail.vue'
    );
const representatives = () =>
    import(/* webpackChunkName: "representatives" */ '@/main/components/representatives.vue');
const organizations = () =>
    import(/* webpackChunkName: "organizations" */ '@/main/components/organizations.vue');
const users = () => import(/* webpackChunkName: "users" */ '@/main/components/users.vue');
const forms = () => import(/* webpackChunkName: "forms" */ '@/main/components/forms.vue');
const form = () => import(/* webpackChunkName: "form" */ '@/main/components/form.vue');
const rules = () => import(/* webpackChunkName: "rules" */ '@/main/components/rules.vue');
const inspection = () =>
    import(/* webpackChunkName: "inspection" */ '@/main/components/inspection/inspection.vue');
const AutoDamageClients = () =>
    import(/* webpackChunkName: "AutoDamageClients" */ '@/main/components/autodamage/clients.vue');
const AutoDamages = () =>
    import(/* webpackChunkName: "AutoDamages" */ '@/main/components/autodamage/damages.vue');
const AutoDamageForm = () =>
    import(/* webpackChunkName: "AutoDamageForm" */ '@/main/components/autodamage/damageform.vue');
const AutoDamageDetail = () =>
    import(
        /* webpackChunkName: "AutoDamageDetail" */ '@/main/components/autodamage/damagedetail.vue'
    );
const VideoCallHome = () =>
    import(/* webpackChunkName: "VideoCallHome" */ '@/main/components/videocall/VideocallHome.vue');
const Meeting = () =>
    import(/* webpackChunkName: "Meeting" */ '@/main/components/videocall/meeting/Meeting.vue');
const Access = () =>
    import(/* webpackChunkName: "Access" */ '@/main/components/sharedentity/access.vue');
const RiskView = () => import(/* webpackChunkName: "RiskView" */ '@/main/components/riskview.vue');
const reports = () => import(/* webpackChunkName: "reports" */ '@/main/components/reports.vue');
const auditlog = () => import(/* webpackChunkName: "auditlog" */ '@/main/components/auditlog.vue');
const filemanager = () =>
    import(/* webpackChunkName: "filemanager" */ '@/main/components/filemanager.vue');
const rolemanagement = () =>
    import(/* webpackChunkName: "rolemanagement" */ '@/main/components/rolemanagement.vue');
const collectionInfo = () =>
    import(/* webpackChunkName: "inspection" */ '@/main/components/uwscreens/collectioninfo.vue');
const claimInfo = () =>
    import(/* webpackChunkName: "inspection" */ '@/main/components/uwscreens/claiminfolist.vue');
const adminreports = () =>
    import(/* webpackChunkName: "reports" */ '@/main/components/adminreports/index.vue');
const sites = () => import(/* webpackChunkName: "sitesPage" */ '@/main/components/sites.vue');

const ClaimDashboard = () =>
    import(/* webpackChunkName: "Claim" */ '@/main/components/claimmanagement/dashboard.vue');
const ClaimDetail = () =>
    import(/* webpackChunkName: "Claim" */ '@/main/components/claimmanagement/claimdetail.vue');

const Acknowledgement = () =>
    import(/* webpackChunkName: "Claim" */ '@/main/components/claimmanagement/acknowledgement.vue');

const ClaimAnalytics = () =>
    import(/* webpackChunkName: "Claim" */ '@/main/components/claimmanagement/analytics.vue');

const ClaimDisplay = () =>
    import(/* webpackChunkName: "Claim" */ '@/main/components/claimmanagement/claimdisplay.vue');

const policies = () => import('@/main/components/inspolicylist.vue');

const store = vuexStore;
const canAccess = (accessRight, to, from, next) => {
    const hasMeta = to.meta && to.meta.name;
    if (accessRight()) {
        if (to.name === 'inspection' && !hasMeta && !store.getters.user.isEngineer) {
            next('/404');
        } else {
            next();
        }
    } else {
        next('/404');
    }
};

const routes = [
    {
        path: '',
        name: 'riskspace',
        component: RiskLayout,
        children: [
            {
                path: '/404',
                component: fourofour,
                name: 'fourofour',
            },
            {
                path: '/dashboard',
                component: dashboard,
                name: 'dashboard',
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('survey_management_dashboard')
                ),
            },
            {
                path: '/clients',
                component: customers,
                name: 'customers',
                props: {
                    customerType: 'VRS',
                },
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('customer_management_customer')
                ),
            },
            {
                path: '/clients/:customerId',
                name: 'customerDetail',
                component: customerDetail,
                props: {
                    customerType: 'VRS',
                },
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('customer_management_customer')
                ),
            },
            {
                path: '/micro-clients',
                component: customers,
                name: 'microCustomers',
                props: {
                    customerType: 'MICRO',
                },
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('customer_management_customer')
                ),
            },
            {
                path: '/micro-clients/:customerId',
                name: 'microCustomerDetail',
                component: customerDetail,
                props: {
                    customerType: 'MICRO',
                },
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('customer_management_customer')
                ),
            },
            {
                path: '/micro-list',
                component: microlist,
                name: 'microList',
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('customer_management_micro_list')
                ),
            },
            {
                path: '/micro-list/:itemId',
                name: 'microListItemDetail',
                component: microlistitemdetail,
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('customer_management_micro_list')
                ),
            },
            {
                path: '/conversations',
                name: 'conversations',
                component: conversations,
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('video_call_history')
                ),
            },
            {
                path: '/appointments',
                name: 'appointments',
                component: appointments,
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('appointment_self', 'appointment_others')
                ),
            },
            {
                path: '/conversations/:itemId',
                name: 'conversationDetail',
                component: conversationdetail,
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('video_call_history')
                ),
            },
            {
                path: '/representatives',
                component: representatives,
                name: 'representatives',
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('agent_management_agent')
                ),
            },
            {
                path: '/representatives/:id',
                name: 'representativeDetail',
                component: representativedetail,
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('agent_management_agent')
                ),
                meta: {
                    cache: false,
                },
            },
            {
                path: '/feeds',
                component: feeds,
                name: 'feeds',
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('survey_management_feeds')
                ),
            },
            {
                path: '/feeds/:inspectionId',
                component: feeds,
                name: 'feeds',
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('survey_management_feeds')
                ),
            },
            {
                path: '/organizations',
                component: organizations,
                name: 'organizations',
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('organization_management_organization')
                ),
            },
            {
                path: '/users',
                component: users,
                name: 'users',
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('organization_management_user')
                ),
            },
            {
                path: '/forms',
                component: forms,
                name: 'forms',
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('form_management_form')
                ),
            },
            {
                path: '/form/:id',
                name: 'form',
                component: form,
                beforeEnter: canAccess.bind(
                    this,
                    () =>
                        store.getters.isContextUserRootAdmin &&
                        store.getters.context.canRead('form_management_form')
                ),
            },
            {
                path: '/rules/:id',
                name: 'rules',
                component: rules,
                beforeEnter: canAccess.bind(
                    this,
                    () =>
                        store.getters.isContextUserRootAdmin &&
                        store.getters.context.canRead('form_management_form')
                ),
            },
            {
                path: '/inspection/:inspectionId',
                name: 'inspection',
                component: inspection,
                props: true,
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('survey_management_inspection')
                ),
                meta: {
                    keepAlive: false,
                },
            },
            {
                path: '/videocall',
                name: 'videocall',
                component: VideoCallHome,
                meta: {
                    title: 'VideoCallHome',
                },
                props: {
                    callType: 'direct',
                },
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canCreate(
                        'video_call_direct',
                        'video_call_agent',
                        'video_call_customer'
                    )
                ),
            },
            {
                path: '/videocall/:appid',
                name: 'videocall',
                component: VideoCallHome,
                meta: {
                    title: 'VideoCallHome',
                },
                props: {
                    callType: 'direct',
                },
                // eslint-disable-next-line sonarjs/no-identical-functions
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canCreate(
                        'video_call_direct',
                        'video_call_agent',
                        'video_call_customer'
                    )
                ),
            },
            {
                path: '/reports',
                component: reports,
                name: 'reports',
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('reports_general')
                ),
            },
            {
                path: '/auditlog',
                component: auditlog,
                name: 'auditlog',
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('organization_management_auditlog')
                ),
            },
            {
                path: '/adminreports',
                component: adminreports,
                name: 'adminreports',
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('org_admin_reports')
                ),
            },
            {
                path: '/filemanager',
                component: filemanager,
                name: 'filemanager',
                beforeEnter: canAccess.bind(this, () => store.getters.isContextUserRootAdmin),
            },
            {
                path: '/roles',
                component: rolemanagement,
                name: 'roles',
                beforeEnter: canAccess.bind(
                    this,
                    () =>
                        store.getters.isContextUserRootAdmin &&
                        store.getters.context.canRead('role_management_role')
                ),
            },
            {
                path: '/claim-dashboard',
                name: 'claimdashboard',
                component: ClaimDashboard,
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('claim_management_dashboard')
                ),
            },
            {
                path: '/claim/:claimId',
                name: 'claimdetail',
                component: ClaimDetail,
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('claim_management')
                ),
                meta: {
                    cache: false,
                },
            },
            {
                path: '/claim-acknowledgement/:claimId',
                name: 'acknowledgement',
                component: Acknowledgement,
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('claim_management_acknowledgement')
                ),
                meta: {
                    cache: false,
                },
            },
            {
                path: '/claim-analytics',
                name: 'claimanalytics',
                component: ClaimAnalytics,
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('claim_management_dashboard')
                ),
                meta: {
                    cache: false,
                },
            },
            {
                path: '/claim-display/:claimId',
                name: 'claimdisplay',
                component: ClaimDisplay,
                beforeEnter: canAccess.bind(this, () =>
                    store.getters.context.canRead('claim_management')
                ),
                meta: {
                    cache: false,
                },
            },
            {
                path: '/sites',
                component: sites,
                name: 'sites',
            },
            {
                path: '/policies',
                name: 'policies',
                component: policies,
                beforeEnter: canAccess.bind(this, () => store.getters.context.canRead('policy')),
            },
        ],
    },
    {
        path: '/share/:shareId/:lang',
        component: SharedEntityLayout,
        children: [
            {
                path: '',
                meta: {
                    name: 'share',
                },
                component: Access,
            },
            {
                path: '/share/:shareId/conversations/:itemId/:lang',
                name: 'conversationDetail',
                meta: {
                    name: 'share',
                },
                component: conversationdetail,
            },
            {
                path: '/share/:shareId/visit/:itemId/:lang',
                name: 'visit',
                meta: {
                    name: 'share',
                },
                component: RiskView,
            },
            {
                path: '/share/:shareId/inspection/:inspectionId/:lang',
                name: 'inspection',
                meta: {
                    name: 'share',
                    addSideNav: true,
                },
                component: inspection,
            },
            {
                path: '/share/:shareId/collection_info/:policyId/:lang',
                name: 'collection_info',
                meta: {
                    name: 'share',
                },
                component: collectionInfo,
            },
            {
                path: '/share/:shareId/claim_info/:policyId/:lang',
                name: 'claim_info',
                meta: {
                    name: 'share',
                },
                component: claimInfo,
            },
        ],
    },
    {
        path: '/videocall/meeting/:mcid',
        name: 'meeting',
        component: Meeting,
        meta: {
            title: 'Meeting',
        },
    },
    {
        path: '/autodamage',
        name: 'autodamage',
        component: AutoDamageLayout,
        children: [
            {
                path: '',
                name: 'policies',
                component: AutoDamageClients,
                meta: {
                    title: 'Policies',
                },
            },
            {
                path: '/autodamage/damages',
                name: 'damages',
                component: AutoDamages,
                meta: {
                    title: 'Damages',
                },
            },
            {
                path: '/autodamage/damage/:id',
                name: 'damage',
                component: AutoDamageDetail,
                meta: {
                    title: 'Damage Detail',
                },
            },
            {
                path: '/autodamage/damageform',
                name: 'damageform',
                component: AutoDamageForm,
                meta: {
                    title: 'Damage Form',
                },
            },
        ],
    },
    {
        path: '/translation',
        name: 'translation',
        component: TranslationLayout,
        beforeEnter: canAccess.bind(this, () => store.getters.isContextUserRootAdmin),
    },
    {
        path: '*',
        component: RiskLayout,
        children: [
            {
                path: '',
                name: 'globalfour0fourpage',
                component: fourofour,
            },
        ],
    },
];

export default routes;
